import { useMutation } from '@tanstack/react-query';
import useCart from 'components/Cart/Cart.hooks';
import { useCallback, useEffect, useRef } from 'react';
import analytics from 'utils/analytics';
import { removeLineItem } from 'utils/api/cartApis';
import { logError } from 'utils/helpers';
import { LineItem } from 'utils/types';

export const REMOVE_QUEST_PRODUCTS_FROM_CART_ANALYTICS_EVENT =
  'REMOVE_QUEST_PRODUCTS_FROM_CART';

export type RemoveQuestProductsProps = {};

/**
 * Sometimes we have quest products listed in regular cart. This component removes Quest Products from the cart.
 * This is a side effect that is triggered when the cart is loaded.
 *
 */
const RemoveQuestProducts = (props: RemoveQuestProductsProps) => {
  const didRunRef = useRef(false);
  const { order, refetchOrder, resetOrder, isFetchingOrder } = useCart();

  const mutation = useMutation(removeLineItem, {
    retry: 0,
  });

  const removeQuestProduct = useCallback(
    async (payload: { lineItemId: number; orderId: number }) => {
      const { lineItemId, orderId } = payload;

      try {
        await mutation.mutateAsync({
          lineItemId,
          orderId,
        });
      } catch (error) {
        logError('Error removing Quest Products from Cart', {
          component: 'RemoveQuestProducts',
          method: 'removeLineItem',
          lineItemId,
          orderId,
          correlation_ids: {
            spree_order_id: orderId,
          },
          error,
        });

        // throw the error so that the parent component can handle it
        throw error;
      }
    },
    [mutation],
  );

  const removeQuestProducts = useCallback(
    async (lineItemsToRemove: LineItem[]) => {
      for (const item of lineItemsToRemove) {
        await removeQuestProduct({
          lineItemId: item.id!,
          orderId: item.order_id!,
        });
      }
    },
    [removeQuestProduct],
  );

  useEffect(() => {
    if (didRunRef.current) {
      return;
    }

    const main = async () => {
      if (!isFetchingOrder && order) {
        didRunRef.current = true;

        const lineItemsToRemove = order.line_items.filter(
          (item) => item.is_lab_visit,
        );

        const isThereAnyQuestroductsInCart = lineItemsToRemove.length > 0;

        if (isThereAnyQuestroductsInCart) {
          // Track the start of the process
          analytics.track({
            event: REMOVE_QUEST_PRODUCTS_FROM_CART_ANALYTICS_EVENT,
            data: {
              status: 'start',
              line_items: lineItemsToRemove,
            },
          });

          try {
            await removeQuestProducts(lineItemsToRemove);
            await refetchOrder?.();
            // Track the success of the process
            analytics.track({
              event: REMOVE_QUEST_PRODUCTS_FROM_CART_ANALYTICS_EVENT,
              data: {
                status: 'success',
                line_items: lineItemsToRemove,
              },
            });
          } catch (error) {
            analytics.track({
              event: REMOVE_QUEST_PRODUCTS_FROM_CART_ANALYTICS_EVENT,
              data: {
                status: 'failed',
                line_items: lineItemsToRemove,
              },
            });

            resetOrder();
          }
        }
      }
    };

    main();
  }, [isFetchingOrder, order, refetchOrder, removeQuestProducts, resetOrder]);

  return null;
};

export default RemoveQuestProducts;
